import { FunctionComponent } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

import { PermissionRoles } from "@/enum";

import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import { useHasPermissions } from "@/utils/useHasPermissions";

import { ROUTE_CONFIG } from "@/routes/config";

import { CompaniesOverview } from "./pages/CompaniesOverview/CompaniesOverview";
import { CompanyDetails } from "../Company/components/CompanyDetails";

/**
 * Only a global admin may open a company other than their own. Everyone else is
 * sent back to their own company rather than shown someone else's (BCM360-334).
 */
const CompanyDetailsGuard: FunctionComponent = () => {
	const { id } = useParams();
	const { user } = useAppSelector(authSelector);
	const { hasPermissions } = useHasPermissions();

	const ownCompanyId = user?.company?.id;
	const requestedCompanyId = id !== undefined && Number.isFinite(+id) ? +id : undefined;
	const mayOpenAnyCompany = hasPermissions([PermissionRoles.GLOBAL_ADMIN]);

	if (requestedCompanyId === undefined) {
		return <Navigate replace to={ROUTE_CONFIG.DASHBOARD} />;
	}

	if (requestedCompanyId !== ownCompanyId && !mayOpenAnyCompany) {
		return (
			<Navigate
				replace
				to={ownCompanyId ? `${ROUTE_CONFIG.COMPANIES}/${ownCompanyId}` : ROUTE_CONFIG.DASHBOARD}
			/>
		);
	}

	return <CompanyDetails />;
};

export const Companies: FunctionComponent = () => {
	const { hasPermissions } = useHasPermissions();

	return (
		<Routes>
			{hasPermissions([PermissionRoles.GLOBAL_ADMIN]) && (
				<Route element={<CompaniesOverview />} path="/" />
			)}
			{hasPermissions([PermissionRoles.ADMIN]) && (
				<Route element={<CompanyDetailsGuard />} path="/:id" />
			)}
		</Routes>
	);
};
